import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronUp, Calendar, Clock, User, Zap, Link, FileText, Video, MapPin, Users, Mail, UserPlus, Loader2, Compass, Search, Eye, X as XIcon, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PRIORITY_META, EFFORT_OPTIONS, getTextColorForBg } from './TaskCard';
import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { PersonPicker } from './PersonPicker';
import DatePickerWithStats from './DatePickerWithStats';

const API = '/api';

const STATUS_OPTIONS = ['To Do', 'In Progress', 'Partial', 'Blocked', 'Done'];

// Calculate effort from duration
const calculateEffortFromDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  const duration = (eh * 60 + em) - (sh * 60 + sm);
  if (duration <= 15) return 1;
  if (duration <= 30) return 2;
  if (duration <= 60) return 3;
  if (duration <= 180) return 5;
  if (duration <= 360) return 8;
  return 13;
};

const TaskModal = ({ open, onOpenChange, task, selectedDate, onSave, defaultValues }) => {
  const { workAreaNames, workAreaColors } = useWorkAreas();
  const WORK_AREA_COLORS = workAreaColors;
  const [form, setForm] = useState({
    task_title: '',
    task_description: '',
    status: 'To Do',
    task_estimated_effort: 1,
    task_actual_effort: null,
    task_actual_personal_effort: null,
    task_priority: null,
    work_area_id: '',
    task_start_time: '',
    task_end_time: '',
    task_is_meeting: false,
    task_delivery_date: null,
    // Extended fields
    impact: '',
    next_steps: '',
    big_picture_id: '',
    responsible_person_id: '',
    responsible_person_name: '',
    observers: [],
    task_related_links: '',
    personal_sub_area: '',
    // Meeting fields
    meeting_location: '',
    meeting_attendees: [],
    // Recurring
    task_is_recurring: false,
    task_recurring_rule: null,
  });
  const [saving, setSaving] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [bigPictureItems, setBigPictureItems] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [personalSubAreas, setPersonalSubAreas] = useState([]);

  // Fetch personal sub areas
  useEffect(() => {
    if (!open) return;
    fetch(`${API}/org/personal-sub-areas`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setPersonalSubAreas)
      .catch(() => {});
  }, [open]);

  // Fetch Big Picture items for linking
  useEffect(() => {
    if (!open || !showMoreDetails) return;
    const fetchBigPicture = async () => {
      try {
        const res = await fetch(`${API}/bigpicture/items`, { credentials: 'include' });
        if (res.ok) {
          const items = await res.json();
          setBigPictureItems(items);
        }
      } catch (e) { /* ignore */ }
    };
    fetchBigPicture();
  }, [open, showMoreDetails]);

  // Fetch org members for person responsible and observers
  useEffect(() => {
    if (!open) return;
    const fetchMembers = async () => {
      try {
        const res = await fetch(`${API}/org/members`, { credentials: 'include' });
        if (res.ok) {
          const members = await res.json();
          setOrgMembers(members);
        }
      } catch (e) { /* ignore */ }
    };
    fetchMembers();
  }, [open]);

  useEffect(() => {
    if (task) {
      setForm({
        task_title: task.task_title || '',
        task_description: task.task_description || '',
        status: task.task_status || 'To Do',
        task_estimated_effort: task.task_estimated_effort || 1,
        task_actual_effort: task.task_actual_effort ?? null,
        task_actual_personal_effort: task.task_actual_personal_effort ?? null,
        task_priority: task.task_priority ?? 3,
        work_area_id: task.work_area_id || 'Personal',
        task_start_time: task.task_local_start_time || '',
        task_end_time: task.task_local_end_time || '',
        task_is_meeting: task.task_is_meeting || false,
        task_delivery_date: task.task_local_date || null,
        impact: task.impact || '',
        next_steps: task.next_steps || '',
        big_picture_id: task.big_picture_id || '',
        responsible_person_id: task.responsible_person_id || '',
        responsible_person_name: task.responsible_person_name || '',
        observers: task.observers || [],
        task_related_links: task.task_related_links || '',
        meeting_location: task.meeting_location || '',
        meeting_attendees: task.meeting_attendees || [],
      });
      if (task.impact || task.next_steps || task.big_picture_id || task.task_related_links || task.observers?.length) {
        setShowMoreDetails(true);
      }
    } else {
      setForm({
        task_title: '',
        task_description: '',
        status: 'To Do',
        task_estimated_effort: defaultValues?.task_estimated_effort || 1,
        task_actual_effort: null,
        task_actual_personal_effort: null,
        task_priority: null,
        work_area_id: '',
        task_start_time: defaultValues?.task_start_time || '',
        task_end_time: defaultValues?.task_end_time || '',
        task_is_meeting: false,
        task_delivery_date: defaultValues?.task_delivery_date || format(selectedDate, 'yyyy-MM-dd'),
        impact: '',
        next_steps: '',
        big_picture_id: '',
        responsible_person_id: '',
        responsible_person_name: '',
        observers: [],
        task_related_links: '',
        meeting_location: '',
        meeting_attendees: [],
      });
      setShowMoreDetails(false);
    }
  }, [task, selectedDate, open, defaultValues]);

  // Handle meeting toggle - auto-set P0 and calculate effort
  const handleMeetingToggle = (checked) => {
    const updates = { task_is_meeting: checked };
    if (checked) {
      updates.task_priority = 0; // P0 for meetings
      const effort = calculateEffortFromDuration(form.task_start_time, form.task_end_time);
      if (effort) updates.task_estimated_effort = effort;
    }
    setForm(f => ({ ...f, ...updates }));
  };

  // Handle time change - auto-calc effort for meetings
  const handleTimeChange = (field, value) => {
    const updates = { [field]: value };
    if (form.task_is_meeting) {
      const startTime = field === 'start_time' ? value : form.task_start_time;
      const endTime = field === 'end_time' ? value : form.task_end_time;
      const effort = calculateEffortFromDuration(startTime, endTime);
      if (effort) updates.task_estimated_effort = effort;
    }
    setForm(f => ({ ...f, ...updates }));
  };

  const handleSave = async () => {
    if (!form.task_title.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  // Add attendee to meeting
  const addAttendee = () => {
    if (!newInviteEmail || !newInviteEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Check if already added
    if (form.meeting_attendees.some(a => a.email === newInviteEmail)) {
      toast.error('This email is already added');
      return;
    }
    
    setForm(f => ({
      ...f,
      meeting_attendees: [
        ...f.meeting_attendees,
        {
          email: newInviteEmail,
          name: newInviteEmail.split('@')[0],
          response_status: 'needsAction',
          organizer: false,
          self: false
        }
      ]
    }));
    setNewInviteEmail('');
    toast.success(`Added ${newInviteEmail} to attendees`);
  };

  // Remove attendee
  const removeAttendee = (email) => {
    setForm(f => ({
      ...f,
      meeting_attendees: f.meeting_attendees.filter(a => a.email !== email)
    }));
  };

  const isCompletedOrPartial = ['Done', 'Partial'].includes(form.task_status);
  const isPersonal = form.work_area_id === 'Personal';
  const priorityMeta = form.task_priority != null ? (PRIORITY_META[String(form.task_priority)] || PRIORITY_META["3"]) : null;
  const workAreaBg = form.work_area_id ? (WORK_AREA_COLORS[form.work_area_id] || WORK_AREA_COLORS['General']) : '#E5E7EB';
  const workAreaText = getTextColorForBg(workAreaBg);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="task-modal">
        <DialogHeader>
          {/* pr-8 prevents overlap with the dialog's built-in close X */}
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="text-xl font-semibold">
              {task ? 'Edit Task' : 'New Task'}
            </DialogTitle>
            {/* Priority & Work Area at top right of header */}
            <div className="flex items-center gap-2">
              <Select 
                value={form.task_priority != null ? String(form.task_priority) : '_none'} 
                onValueChange={(v) => setForm(f => ({ ...f, task_priority: v === '_none' ? null : parseInt(v) }))}
              >
                <SelectTrigger 
                  className="h-9 w-20 font-bold border-0"
                  style={priorityMeta ? { backgroundColor: priorityMeta.bg, color: priorityMeta.text } : { backgroundColor: '#E5E7EB', color: '#6B7280' }}
                  data-testid="task-priority-select"
                >
                  <SelectValue placeholder="P?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">
                    <span className="px-2 py-0.5 rounded font-bold text-xs orka-text-tertiary">None</span>
                  </SelectItem>
                  {Object.entries(PRIORITY_META).map(([v, meta]) => (
                    <SelectItem key={v} value={v}>
                      <span className="px-2 py-0.5 rounded font-bold text-xs" style={{ backgroundColor: meta.bg, color: meta.text }}>
                        {meta.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select 
                value={form.work_area_id || '_none'} 
                onValueChange={(v) => {
                  const newArea = v === '_none' ? '' : v;
                  const updates = { work_area_id: newArea };
                  if (newArea === 'Personal') {
                    updates.task_priority = -1;
                  }
                  setForm(f => ({ ...f, ...updates }));
                }}
              >
                <SelectTrigger 
                  className="h-9 w-auto min-w-36 font-medium border-0 normal-case"
                  style={form.work_area_id ? { backgroundColor: workAreaBg, color: workAreaText } : { backgroundColor: '#E5E7EB', color: '#6B7280' }}
                  data-testid="task-work-area-select"
                >
                  <SelectValue placeholder="Category?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">
                    <span className="px-2 py-0.5 rounded text-xs font-medium orka-text-tertiary">None</span>
                  </SelectItem>
                  {workAreaNames.map(w => (
                    <SelectItem key={w} value={w}>
                      <span 
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: WORK_AREA_COLORS[w] || '#E5E7EB', color: getTextColorForBg(WORK_AREA_COLORS[w] || '#E5E7EB') }}
                      >
                        {w}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Personal Sub Area - only when Personal is selected */}
              {form.work_area_id === 'Personal' && personalSubAreas.length > 0 && (
                <Select value={form.personal_sub_area || '_none'} onValueChange={(v) => setForm(f => ({ ...f, personal_sub_area: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="h-9 w-32 text-xs orka-border-subtle" data-testid="personal-sub-area-select">
                    <SelectValue placeholder="Sub area..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {personalSubAreas.map(sa => (
                      <SelectItem key={sa.sub_area_id} value={sa.name}>
                        <span className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: sa.color || '#E5E7EB' }}>{sa.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <DialogDescription>
            {task 
              ? 'Update the task details below.' 
              : `Create a new task for ${format(selectedDate, 'MMMM d, yyyy')}`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Epic 7 — surface calendar sync conflicts from reconciliation engine */}
        {task && (task?.sync_conflict || (Array.isArray(task?.sync_conflict_fields) && task.sync_conflict_fields.length > 0)) && (
          <div
            data-testid="sync-conflict-banner"
            className="mx-0 mt-2 flex items-start gap-2 rounded-md border orka-border-default orka-bg-danger-soft red-950/30 p-3"
          >
            <AlertTriangle className="w-4 h-4 orka-text-danger mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-xs">
              <div className="font-semibold text-red-800">
                Calendar sync conflict
              </div>
              <div className="orka-text-danger mt-0.5">
                Google and Orka both changed the same field{(task.sync_conflict_fields || []).length !== 1 ? 's' : ''}:{' '}
                <span className="font-mono">
                  {(task.sync_conflict_fields || []).join(', ')}
                </span>
                . Google wins for time fields; Orka wins for title / description / location.
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4 py-4">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="title" className="flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> Title *
            </Label>
            <Input
              id="title"
              value={form.task_title}
              onChange={(e) => setForm(f => ({ ...f, task_title: e.target.value }))}
              placeholder="What needs to be done?"
              className="h-11"
              data-testid="task-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.task_description}
              onChange={(e) => setForm(f => ({ ...f, task_description: e.target.value }))}
              placeholder="Add more details (optional)"
              className="min-h-[80px] resize-none"
              data-testid="task-description-input"
            />
          </div>

          {/* Meeting Toggle */}
          <div className="flex items-center gap-3 py-2 px-3 orka-bg-info-soft blue-900/20 rounded-lg border orka-border-subtle">
            <Checkbox
              id="is-meeting"
              checked={form.task_is_meeting}
              onCheckedChange={handleMeetingToggle}
              className="h-5 w-5"
              data-testid="task-meeting-toggle"
            />
            <div>
              <Label htmlFor="is-meeting" className="text-sm font-medium cursor-pointer">Is Meeting</Label>
              <p className="text-xs orka-text-info">Meetings are auto-set to P0 priority</p>
            </div>
          </div>

          {/* Recurring toggle */}
          {!task && (
            <div className="space-y-2">
              <div className="flex items-center gap-3 py-2 px-3 bg-purple-50 purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <Checkbox
                  id="is-recurring"
                  checked={form.task_is_recurring}
                  onCheckedChange={(checked) => setForm(f => ({
                    ...f,
                    task_is_recurring: checked,
                    task_recurring_rule: checked ? (f.task_recurring_rule || { frequency: 'weekly', count: 4 }) : null
                  }))}
                  className="h-5 w-5"
                  data-testid="task-recurring-toggle"
                />
                <div>
                  <Label htmlFor="is-recurring" className="text-sm font-medium cursor-pointer">Is Recurring</Label>
                  <p className="text-xs orka-text-intent dark:text-purple-400">Auto-create future tasks on a schedule</p>
                </div>
              </div>
              {form.task_is_recurring && form.task_recurring_rule && (
                <div className="grid grid-cols-3 gap-2 px-1">
                  <div className="space-y-1">
                    <Label className="text-xs">Frequency</Label>
                    <Select
                      value={form.task_recurring_rule.frequency || 'weekly'}
                      onValueChange={(v) => setForm(f => ({
                        ...f,
                        task_recurring_rule: { ...f.task_recurring_rule, frequency: v }
                      }))}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Occurrences</Label>
                    <Input
                      type="number"
                      min={1}
                      max={52}
                      value={form.task_recurring_rule.count || 4}
                      onChange={(e) => setForm(f => ({
                        ...f,
                        task_recurring_rule: { ...f.task_recurring_rule, count: parseInt(e.target.value) || 1 }
                      }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">End date</Label>
                    <Input
                      type="date"
                      value={form.task_recurring_rule.end_date || ''}
                      onChange={(e) => setForm(f => ({
                        ...f,
                        task_recurring_rule: { ...f.task_recurring_rule, end_date: e.target.value || null }
                      }))}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Date, Start Time, End Time Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="date" className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Date
              </Label>
              <DatePickerWithStats
                value={form.task_delivery_date}
                onChange={(val) => setForm(f => ({ ...f, task_delivery_date: val || null }))}
                triggerClassName="h-10 w-full"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="start-time" className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Start
              </Label>
              <Input
                id="start-time"
                type="time"
                value={form.task_start_time}
                onChange={(e) => handleTimeChange('start_time', e.target.value)}
                className="h-10 font-mono text-sm"
                data-testid="task-start-time"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-time" className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> End
              </Label>
              <Input
                id="end-time"
                type="time"
                value={form.task_end_time}
                onChange={(e) => handleTimeChange('end_time', e.target.value)}
                className="h-10 font-mono text-sm"
                data-testid="task-end-time"
              />
            </div>
          </div>

          {/* Status and Effort Row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select 
                value={form.task_status} 
                onValueChange={(v) => setForm(f => ({ ...f, status: v }))}
              >
                <SelectTrigger className="h-10" data-testid="task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Est. Effort</Label>
              <Select 
                value={String(form.task_estimated_effort)} 
                onValueChange={(v) => setForm(f => ({ ...f, task_estimated_effort: parseInt(v) }))}
              >
                <SelectTrigger className="h-10" data-testid="task-effort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EFFORT_OPTIONS.map(e => (
                    <SelectItem key={e.value} value={String(e.value)}>{e.value} ({e.minutes}m)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 5px gap before meeting section */}
          {form.task_is_meeting && <div className="h-[5px]" />}

          {/* Actual Effort Row - Only show when Done or Partial */}
          {isCompletedOrPartial && (
            <div className="grid grid-cols-2 gap-3 p-3 orka-bg-success-soft rounded-lg border orka-border-subtle">
              <div className="space-y-1.5">
                <Label>Actual Effort</Label>
                <Select 
                  value={form.task_actual_effort != null ? String(form.task_actual_effort) : '_none'} 
                  onValueChange={(v) => setForm(f => ({ ...f, task_actual_effort: v === '_none' ? null : parseInt(v) }))}
                >
                  <SelectTrigger className="h-10 orka-surface-raised" data-testid="task-actual-effort-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Not set</SelectItem>
                    {EFFORT_OPTIONS.map(e => (
                      <SelectItem key={e.value} value={String(e.value)}>{e.value} ({e.minutes}m)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className={!isPersonal ? 'opacity-50' : ''}>Personal Effort</Label>
                <Select 
                  value={form.task_actual_personal_effort != null ? String(form.task_actual_personal_effort) : '_none'} 
                  onValueChange={(v) => setForm(f => ({ ...f, task_actual_personal_effort: v === '_none' ? null : parseInt(v) }))}
                  disabled={!isPersonal}
                >
                  <SelectTrigger className={`h-10 bg-white ${!isPersonal ? 'opacity-50' : ''}`} data-testid="task-actual-personal-effort-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Not set</SelectItem>
                    {EFFORT_OPTIONS.map(e => (
                      <SelectItem key={e.value} value={String(e.value)}>{e.value} ({e.minutes}m)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Meeting Details Section - Shows when Is Meeting is checked */}
          {form.task_is_meeting && (
            <div className="bg-gradient-to-br from-blue-100 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/20 rounded-xl p-4 space-y-4 border orka-border-subtle">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Meeting Details
              </h3>

              {/* Location */}
              <div className="space-y-1.5">
                <Label className="orka-text-info flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> Location
                </Label>
                <Input
                  value={form.meeting_location}
                  onChange={(e) => setForm(f => ({ ...f, meeting_location: e.target.value }))}
                  placeholder="Meeting room, address, or virtual link..."
                  className="orka-surface-raised orka-border-subtle"
                />
              </div>

              {/* Attendees */}
              <div className="space-y-2">
                <Label className="orka-text-info flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> Attendees ({form.meeting_attendees.length})
                </Label>
                
                {/* Add attendee input */}
                <div className="flex gap-2">
                  <Input
                    value={newInviteEmail}
                    onChange={(e) => setNewInviteEmail(e.target.value)}
                    placeholder="Enter email to add..."
                    className="orka-surface-raised orka-border-subtle flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && addAttendee()}
                  />
                  <Button 
                    type="button"
                    onClick={addAttendee} 
                    className="orka-bg-info hover:bg-blue-700"
                    disabled={!newInviteEmail}
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Attendees list */}
                {form.meeting_attendees.length > 0 ? (
                  <div className="orka-surface-raised rounded-lg border orka-border-subtle divide-y divide-blue-100 dark:divide-blue-800 max-h-[150px] overflow-y-auto">
                    {form.meeting_attendees.map((a, i) => (
                      <div key={i} className="flex items-center justify-between p-2 hover:orka-bg-info-soft/50 dark:hover:bg-blue-900/20">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium orka-text-primary">{a.name}</p>
                            <p className="text-xs orka-text-tertiary">{a.email}</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttendee(a.email)}
                          className="orka-text-danger hover:orka-text-danger hover:orka-bg-danger-soft h-7 px-2"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-3 orka-text-tertiary text-sm orka-surface-raised rounded-lg border orka-border-subtle">
                    No attendees added yet
                  </div>
                )}

                <p className="text-xs orka-text-info">
                  Attendees will receive calendar invites when the meeting is created
                </p>
              </div>
            </div>
          )}

          {/* Add More Details Expander */}
          <button
            type="button"
            onClick={() => setShowMoreDetails(!showMoreDetails)}
            className="w-full flex items-center justify-center gap-2 py-2 px-4 orka-surface-subtle hover:bg-stone-200 rounded-lg text-sm font-medium orka-text-secondary transition-colors"
            data-testid="add-more-details-toggle"
          >
            {showMoreDetails ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide Details
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Add More Details
              </>
            )}
          </button>

          {/* Expanded Details Section */}
          {showMoreDetails && (
            <div className="space-y-4 pt-2 border-t orka-border-subtle">
              {/* Created By (read-only, only on existing tasks) */}
              {task?.created_by_display && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded-lg" data-testid="task-created-by">
                  <FileText className="w-3.5 h-3.5" />
                  <span>Created by <span className="font-medium text-foreground">{task.created_by_display}</span></span>
                </div>
              )}

              {/* Responsible Person (structured, linked to org members) */}
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" /> Responsible Person
                </Label>
                <PersonPicker
                  value={form.responsible_person_id}
                  onChange={(userId, member) => {
                    setForm(f => ({
                      ...f,
                      responsible_person_id: userId,
                      responsible_person_name: member?.name || member?.email || '',
                    }));
                  }}
                  orgMembers={orgMembers}
                  placeholder="Select responsible person..."
                  data-testid="task-responsible-person"
                />
              </div>

              {/* Observers */}
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1">
                  <Eye className="w-3.5 h-3.5" /> Observers ({form.observers.length})
                </Label>
                <PersonPicker
                  value={form.observers}
                  onChange={(observers) => setForm(f => ({ ...f, observers }))}
                  orgMembers={orgMembers}
                  multi
                  placeholder="Add observer..."
                  data-testid="task-observers"
                />
              </div>

              {/* Impact */}
              <div className="space-y-1.5">
                <Label htmlFor="impact">Impact</Label>
                <Textarea
                  id="impact"
                  value={form.impact}
                  onChange={(e) => setForm(f => ({ ...f, impact: e.target.value }))}
                  placeholder="What is the business impact of this task?"
                  className="min-h-[60px] resize-none"
                  data-testid="task-impact-input"
                />
              </div>

              {/* Next Steps */}
              <div className="space-y-1.5">
                <Label htmlFor="next-steps">Next Steps</Label>
                <Textarea
                  id="next-steps"
                  value={form.next_steps}
                  onChange={(e) => setForm(f => ({ ...f, next_steps: e.target.value }))}
                  placeholder="What are the action items or follow-ups?"
                  className="min-h-[60px] resize-none"
                  data-testid="task-next-steps-input"
                />
              </div>

              {/* Related Epic/Project/Goal */}
              <div className="space-y-1.5">
                <Label htmlFor="related-epic" className="flex items-center gap-1">
                  <Compass className="w-3.5 h-3.5" /> Related Epic/Project/Goal
                </Label>
                <Select
                  value={form.big_picture_id || '_none'}
                  onValueChange={(v) => setForm(f => ({ ...f, big_picture_id: v === '_none' ? '' : v }))}
                >
                  <SelectTrigger className="h-10" data-testid="task-related-epic-input">
                    <SelectValue placeholder="Link to Big Picture..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="_none">None</SelectItem>
                    {bigPictureItems.map(bp => (
                      <SelectItem key={bp.item_id} value={bp.title}>
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-normal capitalize">{bp.type}</Badge>
                          <span className="truncate">{bp.title}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Related Links */}
              <div className="space-y-1.5">
                <Label htmlFor="related-links" className="flex items-center gap-1">
                  <Link className="w-3.5 h-3.5" /> Related Links
                </Label>
                <Input
                  id="related-links"
                  value={form.task_related_links}
                  onChange={(e) => setForm(f => ({ ...f, task_related_links: e.target.value }))}
                  placeholder="URLs (comma separated)"
                  className="h-10"
                  data-testid="task-related-links-input"
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="rounded-lg"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!form.task_title.trim() || saving}
            className="rounded-lg bg-slate-900 slate-100 text-white dark:orka-text-primary hover:bg-slate-800 dark:hover:bg-slate-200"
            data-testid="save-task-button"
          >
            {saving ? 'Saving...' : task ? 'Update Task' : 'Create Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskModal;
